import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Plus, Edit, Trash2, Play, Pause, Clock, Mail, AlertCircle, Database, BarChart, FileText, UserCheck, ChevronRight, ChevronDown, Users, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  getReportSubscriptionsByUser,
  getReportSubscriptionsByOrg,
  deleteReportSubscription,
  toggleReportSubscriptionStatus,
  testReportSubscription,
  testNativePagesReportSubscription,
  getNativeReportSubscriptions,
  deleteNativeReportSubscription,
  toggleNativeReportSubscriptionStatus,
  testNativeReportSubscription
} from '../../utils/api';
import NativePagesScheduleEditModal from './NativePagesScheduleEditModal';
// The in-report drawer writes these shapes; read them back with the same code
// so the list can never describe a schedule differently from the drawer.
import { summarizeSchedule } from '../ReportsNative/schedule/nextRuns';
import { periodLabel } from '../ReportsNative/schedule/periods';
import { PAGE_TIERS } from '../ReportsNative/schedule/rowDepth';

// Three subscription pipelines share this page:
//  'excel'       — nativeReportSubscription container (daily-performance /
//                  daily-sales-labor Excel emails)
//  'nativePages' — report-subscription docs rendered as the native PDF book
//                  (the Power BI replacement; may still carry a legacy reportId
//                  if migrated)
//  'powerbi'     — legacy report-subscription docs
const subKind = (sub) => {
  if (sub._source === 'excel') return 'excel';
  if (sub.reportType === 'nativePages') return 'nativePages';
  return 'powerbi';
};

const getYesterdayLocal = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const UnifiedSubscriptionManagement = ({ currentUser, currentOrganization, onCreateNew, onEdit, refreshTrigger }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [nativeSubscriptions, setNativeSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testModal, setTestModal] = useState(null); // { subscription, isNative, date }
  const [actionLoading, setActionLoading] = useState({});
  const [expandedBatches, setExpandedBatches] = useState(new Set()); // Track which batch groups are expanded
  const [editingNativePages, setEditingNativePages] = useState(null); // nativePages sub being edited in place
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedSubscriptionId = searchParams.get('edit');
  // True once a load has actually run for a signed-in user + org, so ?edit=
  // never resolves against the empty list of a load that had nothing to fetch.
  const [listReady, setListReady] = useState(false);

  useEffect(() => {
    loadAllSubscriptions();
  }, [currentUser, currentOrganization, refreshTrigger]);

  // ?edit=<id> (the report email's "Manage this subscription" and snapshot
  // cells) opens that subscription's editor once the list has loaded.
  useEffect(() => {
    if (!linkedSubscriptionId || loading || !listReady) return;
    const linked = [...subscriptions, ...nativeSubscriptions].find(sub => String(sub.id) === linkedSubscriptionId);
    if (linked) {
      if (subKind(linked) === 'nativePages') setEditingNativePages(linked);
      else onEdit(linked);
    } else if (!error) {
      toast.error("That subscription isn't in your list for this organization. It may belong to another user, or it has been deleted.");
    }
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  }, [linkedSubscriptionId, loading, listReady, subscriptions, nativeSubscriptions, error]);

  const loadAllSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load Power BI subscriptions
      let powerBIData = [];
      if (currentUser?.isAdmin && currentOrganization) {
        powerBIData = await getReportSubscriptionsByOrg(currentOrganization.id, currentUser.email);
      } else if (currentUser?.email && currentOrganization) {
        powerBIData = await getReportSubscriptionsByUser(currentUser.email, currentOrganization.id);
      }

      // Load Native subscriptions - fetch both own subscriptions and delegated ones
      let nativeData = [];
      if (currentUser?.email && currentOrganization) {
        // Fetch user's own subscriptions
        const ownParams = {
          userId: currentUser.email,
          orgId: currentOrganization.id,
          isActive: undefined
        };
        const ownSubscriptions = await getNativeReportSubscriptions(ownParams);

        // Fetch delegated subscriptions (created by current user for others)
        const delegatedParams = {
          createdBy: currentUser.email,
          orgId: currentOrganization.id,
          isActive: undefined
        };
        const delegatedSubscriptions = await getNativeReportSubscriptions(delegatedParams);

        // Combine both, removing duplicates
        const subscriptionMap = new Map();
        [...ownSubscriptions, ...delegatedSubscriptions].forEach(sub => {
          subscriptionMap.set(sub.id, sub);
        });
        nativeData = Array.from(subscriptionMap.values());

        // Calculate batch counts for subscriptions with batchId
        const batchCounts = {};
        nativeData.forEach(sub => {
          if (sub.batchId) {
            batchCounts[sub.batchId] = (batchCounts[sub.batchId] || 0) + 1;
          }
        });

        // Add batch count to each subscription, and stamp the pipeline so rows
        // can't be confused with nativePages docs (which also lack a reportId).
        nativeData = nativeData.map(sub => {
          const stamped = { ...sub, _source: 'excel' };
          if (sub.batchId && batchCounts[sub.batchId] > 1) {
            stamped._batchCount = batchCounts[sub.batchId];
          }
          return stamped;
        });
      }

      setSubscriptions(powerBIData || []);
      setNativeSubscriptions(nativeData || []);
      if (currentUser?.email && currentOrganization) setListReady(true);
    } catch (err) {
      console.error('Error loading subscriptions:', err);
      setError('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (subscription, kind = 'powerbi') => {
    const newStatus = !subscription.isActive;
    const actionKey = `toggle-${subscription.id}`;

    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));

      let updatedSubscription;
      if (kind === 'excel') {
        updatedSubscription = await toggleNativeReportSubscriptionStatus(subscription.id, newStatus);
        setNativeSubscriptions(prev =>
          prev.map(sub =>
            sub.id === subscription.id ? { ...updatedSubscription, _source: 'excel' } : sub
          )
        );
      } else {
        updatedSubscription = await toggleReportSubscriptionStatus(subscription.id, newStatus);
        setSubscriptions(prev =>
          prev.map(sub =>
            sub.id === subscription.id ? updatedSubscription : sub
          )
        );
      }
    } catch (err) {
      console.error('Error toggling subscription status:', err);
      setError(`Failed to ${newStatus ? 'activate' : 'deactivate'} subscription`);
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleDelete = async (subscription, kind = 'powerbi') => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) {
      return;
    }

    const actionKey = `delete-${subscription.id}`;

    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));

      if (kind === 'excel') {
        await deleteNativeReportSubscription(subscription.id);
        setNativeSubscriptions(prev => prev.filter(sub => sub.id !== subscription.id));
      } else {
        await deleteReportSubscription(subscription.id);
        setSubscriptions(prev => prev.filter(sub => sub.id !== subscription.id));
      }
    } catch (err) {
      console.error('Error deleting subscription:', err);
      setError('Failed to delete subscription');
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleTest = (subscription, kind = 'powerbi') => {
    if (kind === 'excel') {
      // Show date picker modal for Excel-native reports
      setTestModal({ subscription, isNative: true, date: getYesterdayLocal() });
    } else {
      // Power BI and nativePages tests don't need a date — fire immediately
      handleTestExecute(subscription, kind, null);
    }
  };

  const handleTestExecute = async (subscription, kind, date) => {
    const actionKey = `test-${subscription.id}`;
    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));
      if (kind === 'excel') {
        const result = await testNativeReportSubscription(subscription.id, date);
        toast.success(`Test report sent for ${date} to ${result.recipients?.length || 0} recipient${result.recipients?.length !== 1 ? 's' : ''}`, { duration: 4000 });
      } else if (kind === 'nativePages') {
        const result = await testNativePagesReportSubscription(subscription.id);
        toast.success(`Test PDF sent — ${result.pageCount || 0} page${result.pageCount !== 1 ? 's' : ''}. Check your inbox.`, { duration: 4000 });
      } else {
        await testReportSubscription(subscription.id);
        toast.success('Test email sent! Check your inbox.', { duration: 4000 });
      }
    } catch (err) {
      console.error('Error testing subscription:', err);
      toast.error('Failed to send test email. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleTestSubmit = () => {
    if (!testModal) return;
    const { subscription, date } = testModal;
    setTestModal(null);
    handleTestExecute(subscription, 'excel', date);
  };

  // Batch operations
  const toggleBatchExpansion = (batchId) => {
    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return newSet;
    });
  };

  const handleBatchToggleStatus = async (batchSubscriptions, newStatus) => {
    const actionKey = `batch-toggle-${batchSubscriptions[0].batchId}`;

    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));

      // Toggle each subscription in the batch
      const promises = batchSubscriptions.map(sub =>
        toggleNativeReportSubscriptionStatus(sub.id, newStatus)
      );

      const updatedSubs = await Promise.all(promises);

      // Update state
      setNativeSubscriptions(prev =>
        prev.map(sub => {
          const updated = updatedSubs.find(u => u.id === sub.id);
          return updated || sub;
        })
      );
    } catch (err) {
      console.error('Error toggling batch status:', err);
      setError(`Failed to ${newStatus ? 'activate' : 'pause'} batch subscriptions`);
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleBatchDelete = async (batchSubscriptions) => {
    const batchId = batchSubscriptions[0].batchId;
    const count = batchSubscriptions.length;

    if (!window.confirm(`Are you sure you want to delete all ${count} subscriptions in this group?`)) {
      return;
    }

    const actionKey = `batch-delete-${batchId}`;

    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));

      // Delete each subscription in the batch
      const promises = batchSubscriptions.map(sub =>
        deleteNativeReportSubscription(sub.id)
      );

      await Promise.all(promises);

      // Remove from state
      const deletedIds = new Set(batchSubscriptions.map(s => s.id));
      setNativeSubscriptions(prev => prev.filter(sub => !deletedIds.has(sub.id)));
    } catch (err) {
      console.error('Error deleting batch:', err);
      setError('Failed to delete batch subscriptions');
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleBatchTest = async (batchSubscriptions) => {
    const batchId = batchSubscriptions[0].batchId;
    const actionKey = `batch-test-${batchId}`;

    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));

      // Test each subscription in the batch
      const promises = batchSubscriptions.map(sub =>
        testNativeReportSubscription(sub.id)
      );

      const results = await Promise.all(promises);

      toast.success(`Test reports sent to all ${batchSubscriptions.length} subscriptions!`, {
        duration: 4000
      });
    } catch (err) {
      console.error('Error testing batch:', err);
      toast.error('Failed to send test emails for batch. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const formatSchedule = (triggerTime, isLegacyExcel = false) => {
    if (!triggerTime) return 'No schedule';
    // The legacy Excel pipeline stores one `time`, not a `times` list.
    if (isLegacyExcel && !triggerTime.times) {
      const frequency = triggerTime.frequency || 'daily';
      return `${frequency.charAt(0).toUpperCase()}${frequency.slice(1)} · ${triggerTime.time || '—'}`;
    }
    return summarizeSchedule(triggerTime);
  };

  // What the schedule covers: the attachment's window, plus the email body's
  // when the two were unlinked in the drawer.
  const formatTimeframe = (subscription, kind) => {
    if (kind !== 'nativePages') return null;
    const attachment = subscription.reportConfig?.periodLabel || periodLabel(subscription.period);
    const snapshot = subscription.reportConfig?.snapshotPeriod;
    return { attachment: attachment || '—', snapshot: snapshot ? periodLabel(snapshot) : null };
  };

  // What rides along: format, and how far the rows were opened.
  const formatAttachment = (subscription, kind) => {
    if (kind === 'excel') return { label: 'Excel', detail: null };
    if (kind !== 'nativePages') return { label: 'PDF', detail: null };
    const format = subscription.deliveryConfig?.emailConfig?.attachmentFormat;
    if (format === 'xlsx') return { label: 'Excel', detail: 'by property' };
    const pageKind = Array.isArray(subscription.pageKinds) ? subscription.pageKinds[0] : subscription.pageKinds;
    const tiers = PAGE_TIERS[pageKind];
    const level = subscription.reportConfig?.expandLevel;
    return {
      label: 'PDF book',
      detail: tiers && Number.isInteger(level) ? `to ${tiers[Math.min(level, tiers.length - 1)]}` : null,
    };
  };

  const formatNextExecution = (nextScheduled, timezone) => {
    if (!nextScheduled) return 'Not scheduled';

    const date = new Date(nextScheduled);
    const now = new Date();

    if (date < now) {
      return 'Overdue';
    }

    // Display in the user's saved timezone if available
    if (timezone) {
      return date.toLocaleString('en-US', { timeZone: timezone, dateStyle: 'medium', timeStyle: 'short' });
    }

    return date.toLocaleString();
  };

  const getStatusBadge = (subscription) => {
    if (subscription.isDeleted) {
      return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">Deleted</span>;
    }

    if (!subscription.isActive) {
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">Paused</span>;
    }

    return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">Active</span>;
  };

  const getReportTypeLabel = (reportType) => {
    const labels = {
      'daily-performance': 'Daily Performance',
      'daily-sales-labor': 'Daily Sales & Labor'
    };
    return labels[reportType] || reportType;
  };

  const handleBatchEdit = (batchSubscriptions) => {
    // Edit the first subscription but mark it to apply to the whole batch
    const first = batchSubscriptions[0];
    // Add a flag to indicate this is a batch edit
    const subscriptionWithBatchEditFlag = {
      ...first,
      _batchEdit: true // Special flag to pre-select "apply to batch" checkbox
    };
    onEdit(subscriptionWithBatchEditFlag);
  };

  const renderBatchGroupRow = (batchId, batchSubscriptions) => {
    const isExpanded = expandedBatches.has(batchId);
    const count = batchSubscriptions.length;
    const allActive = batchSubscriptions.every(sub => sub.isActive);
    const allPaused = batchSubscriptions.every(sub => !sub.isActive);
    const mixedStatus = !allActive && !allPaused;

    // Get common info from first subscription
    const first = batchSubscriptions[0];
    const actionKey = `batch-toggle-${batchId}`;

    return (
      <React.Fragment key={`batch-${batchId}`}>
        {/* Batch Group Row */}
        <tr className="bg-[var(--lodge-onyx-50)] hover:bg-[var(--lodge-onyx-100)] border-l-4 border-[var(--lodge-onyx-300)]">
          <td className="px-6 py-4">
            <div className="flex items-center">
              <button
                onClick={() => toggleBatchExpansion(batchId)}
                className="mr-2 p-1 hover:bg-[var(--lodge-onyx-200)] rounded transition-colors"
              >
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              <Users size={16} className="mr-2 text-[var(--lodge-onyx-500)] flex-shrink-0" />
              <div>
                <div className="text-sm font-semibold text-gray-900 flex items-center">
                  {first.deliveryConfig?.customSubject || getReportTypeLabel(first.reportType)}
                  <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-[var(--lodge-onyx-200)] text-[var(--lodge-onyx-700)]">
                    {count} users
                  </span>
                </div>
              </div>
            </div>
          </td>
          <td className="px-6 py-4 text-sm text-gray-500">—</td>
          <td className="px-6 py-4 text-sm text-gray-900">Excel</td>
          <td className="px-6 py-4">
            <div className="text-sm text-gray-900">{formatSchedule(first.triggerTime, true)}</div>
            {first.triggerTime?.timezone && (
              <div className="text-xs text-gray-500 mt-1">{first.triggerTime.timezone}</div>
            )}
          </td>
          <td className="px-6 py-4">
            <div className="flex items-center text-sm text-gray-900">
              <Clock size={16} className="mr-2 text-gray-400" />
              {formatNextExecution(first.nextScheduled, first.triggerTime?.timezone)}
            </div>
          </td>
          <td className="px-6 py-4">
            <div className="text-sm text-gray-700">
              {count} recipient{count !== 1 ? 's' : ''}
            </div>
          </td>
          <td className="px-6 py-4">
            {mixedStatus ? (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">Mixed</span>
            ) : allActive ? (
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">Active</span>
            ) : (
              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">Paused</span>
            )}
          </td>
          <td className="px-6 py-4">
            <div className="flex items-center space-x-2">
              {/* Batch Toggle Status */}
              <button
                onClick={() => handleBatchToggleStatus(batchSubscriptions, !allActive)}
                disabled={actionLoading[actionKey]}
                className={`p-2 rounded ${
                  allActive
                    ? 'text-yellow-600 hover:bg-yellow-50'
                    : 'text-green-600 hover:bg-green-50'
                } transition-colors disabled:opacity-50`}
                title={allActive ? 'Pause all' : 'Activate all'}
              >
                {allActive ? <Pause size={16} /> : <Play size={16} />}
              </button>

              {/* Batch Test */}
              <button
                onClick={() => handleBatchTest(batchSubscriptions)}
                disabled={actionLoading[`batch-test-${batchId}`] || !allActive}
                className="p-2 text-orange-600 hover:bg-orange-50 rounded transition-colors disabled:opacity-50"
                title="Test all subscriptions"
              >
                {actionLoading[`batch-test-${batchId}`] ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Mail size={16} />
                )}
              </button>

              {/* Batch Edit */}
              <button
                onClick={() => handleBatchEdit(batchSubscriptions)}
                className="p-2 text-[var(--lodge-onyx-500)] hover:bg-[var(--lodge-onyx-50)] rounded transition-colors disabled:opacity-50"
                title="Edit all subscriptions"
              >
                <Edit size={16} />
              </button>

              {/* Batch Delete */}
              <button
                onClick={() => handleBatchDelete(batchSubscriptions)}
                disabled={actionLoading[`batch-delete-${batchId}`]}
                className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                title="Delete all subscriptions"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </td>
        </tr>

        {/* Individual Subscription Rows (when expanded) */}
        {isExpanded && batchSubscriptions.map(sub => renderSubscriptionRow(sub, 'excel', true))}
      </React.Fragment>
    );
  };

  const renderSubscriptionRow = (subscription, kind = 'powerbi', isIndented = false) => {
    const key = `${kind}-${subscription.id}`;
    const isNative = kind === 'excel';

    // Check if this is a delegated subscription (created by someone else)
    const isDelegated = isNative && subscription.metadata?.isDelegated;
    const createdBy = isNative && subscription.metadata?.createdBy;

    const timeframe = formatTimeframe(subscription, kind);
    const attachment = formatAttachment(subscription, kind);

    const title = kind === 'excel'
      ? (subscription.deliveryConfig?.customSubject || getReportTypeLabel(subscription.reportType))
      : kind === 'nativePages'
        ? (subscription.name || subscription.deliveryConfig?.emailConfig?.customSubject || `${subscription.reportName || 'Report'} PDF`)
        : (subscription.deliveryConfig?.emailConfig?.customSubject || 'Untitled Report');

    return (
      <tr key={key} className={`hover:bg-gray-50 ${isIndented ? 'bg-gray-50/50' : ''}`}>
        <td className="px-6 py-4">
          <div className="flex items-center">
            {isIndented && <div className="w-6 flex-shrink-0"></div>}
            {kind === 'excel' ? (
              <Database size={16} className="mr-2 text-orange-500 flex-shrink-0" />
            ) : kind === 'nativePages' ? (
              <FileText size={16} className="mr-2 text-orange-500 flex-shrink-0" />
            ) : (
              <BarChart size={16} className="mr-2 text-[var(--lodge-onyx-500)] flex-shrink-0" />
            )}
            <div>
              <div className="text-sm font-medium text-gray-900 flex items-center">
                {title}
                {isDelegated && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[var(--lodge-onyx-100)] text-[var(--lodge-onyx-600)]">
                    <UserCheck size={12} className="mr-1" />
                    Delegated
                  </span>
                )}
              </div>
              {isDelegated && createdBy && (
                <div className="text-xs text-gray-400 mt-1">
                  Created by {createdBy}
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="px-6 py-4">
          {timeframe ? (
            <>
              <div className="text-sm text-gray-900">{timeframe.attachment}</div>
              {timeframe.snapshot && (
                <div className="text-xs text-gray-500 mt-1">Body: {timeframe.snapshot}</div>
              )}
            </>
          ) : (
            <span className="text-sm text-gray-500">—</span>
          )}
        </td>
        <td className="px-6 py-4">
          <div className="text-sm text-gray-900">{attachment.label}</div>
          {attachment.detail && <div className="text-xs text-gray-500 mt-1">{attachment.detail}</div>}
        </td>
        <td className="px-6 py-4">
          <div className="text-sm text-gray-900">{formatSchedule(subscription.triggerTime, isNative)}</div>
          {subscription.triggerTime?.timezone && (
            <div className="text-xs text-gray-500 mt-1">{subscription.triggerTime.timezone}</div>
          )}
        </td>
        <td className="px-6 py-4">
          <div className="flex items-center text-sm text-gray-900">
            <Clock size={16} className="mr-2 text-gray-400" />
            {formatNextExecution(subscription.nextScheduled, subscription.triggerTime?.timezone)}
          </div>
          {subscription.lastExecuted && (
            <div className="text-xs text-gray-500 mt-1">
              Last: {new Date(subscription.lastExecuted).toLocaleString('en-US', {
                timeZone: subscription.triggerTime?.timezone,
                dateStyle: 'medium',
                timeStyle: 'short'
              })}
            </div>
          )}
        </td>
        <td className="px-6 py-4">
          <div className="text-sm text-gray-900">
            {isNative ? (
              <>
                {/* Always show the user email for this subscription */}
                <div className={isDelegated ? "font-medium text-[var(--lodge-onyx-700)]" : ""}>{subscription.userId}</div>
                {/* Only show recipient count for non-batch subscriptions */}
                {!subscription.batchId && (
                  <div className="text-xs text-gray-500 mt-1">
                    {subscription.deliveryConfig?.recipients?.length || 0} recipient{subscription.deliveryConfig?.recipients?.length !== 1 ? 's' : ''}
                    {subscription.deliveryConfig?.ccRecipients?.length > 0 && (
                      <span> (+{subscription.deliveryConfig.ccRecipients.length} CC)</span>
                    )}
                  </div>
                )}
                {/* Show CC count for batch subscriptions */}
                {subscription.batchId && subscription.deliveryConfig?.ccRecipients?.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    +{subscription.deliveryConfig.ccRecipients.length} CC
                  </div>
                )}
                {subscription.deliveryConfig?.includeExcelAttachment && (
                  <div className="text-xs text-green-600 mt-1">
                    Excel attached
                  </div>
                )}
              </>
            ) : (
              <>
                {subscription.userId}
                {subscription.deliveryConfig?.emailConfig?.ccRecipients?.length > 0 && (
                  <div className="text-xs text-gray-500">
                    +{subscription.deliveryConfig.emailConfig.ccRecipients.length} CC
                  </div>
                )}
              </>
            )}
          </div>
        </td>
        <td className="px-6 py-4">
          <div>
            {getStatusBadge(subscription)}
            {subscription.executionHistory?.consecutiveFailures > 0 && (
              <div className="text-xs text-red-600 mt-1">
                {subscription.executionHistory.consecutiveFailures} consecutive failures
              </div>
            )}
          </div>
        </td>
        <td className="px-6 py-4">
          <div className="flex items-center space-x-2">
            {/* Toggle Status Button */}
            <button
              onClick={() => handleToggleStatus(subscription, kind)}
              disabled={actionLoading[`toggle-${subscription.id}`] || subscription.isDeleted}
              className={`p-2 rounded ${
                subscription.isActive
                  ? 'text-yellow-600 hover:bg-yellow-50'
                  : 'text-green-600 hover:bg-green-50'
              } transition-colors disabled:opacity-50`}
              title={subscription.isActive ? 'Pause' : 'Activate'}
            >
              {subscription.isActive ? <Pause size={16} /> : <Play size={16} />}
            </button>

            {/* Test Button */}
            <button
              onClick={() => handleTest(subscription, kind)}
              disabled={actionLoading[`test-${subscription.id}`] || !subscription.isActive || subscription.isDeleted}
              className={`p-2 ${kind !== 'powerbi' ? 'text-orange-600 hover:bg-orange-50' : 'text-[var(--lodge-onyx-500)] hover:bg-[var(--lodge-onyx-50)]'} rounded transition-colors disabled:opacity-50`}
              title="Send test email"
            >
              {actionLoading[`test-${subscription.id}`] ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Mail size={16} />
              )}
            </button>

            {/* Edit Button */}
            <button
              onClick={() => kind === 'nativePages' ? setEditingNativePages(subscription) : onEdit(subscription)}
              disabled={subscription.isDeleted}
              className="p-2 text-gray-600 hover:bg-gray-50 rounded transition-colors disabled:opacity-50"
              title="Edit"
            >
              <Edit size={16} />
            </button>

            {/* Delete Button */}
            <button
              onClick={() => handleDelete(subscription, kind)}
              disabled={actionLoading[`delete-${subscription.id}`] || subscription.isDeleted}
              className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
              title="Delete"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const getAllSubscriptions = () => {
    return [...subscriptions, ...nativeSubscriptions].sort((a, b) => {
      // Sort by creation date, newest first
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return dateB - dateA;
    });
  };

  const getDisplayedSubscriptions = () => {
    // Show all subscriptions where user is either:
    // 1. The recipient (userId matches)
    // 2. The creator (for delegated subscriptions)
    return getAllSubscriptions().filter(sub => {
      const isRecipient = sub.userId === currentUser?.email;
      const isCreator = sub.metadata?.createdBy === currentUser?.email || sub.createdBy === currentUser?.email;
      return isRecipient || isCreator;
    });
  };

  // Group subscriptions by batchId for display
  const groupSubscriptionsByBatch = (subs) => {
    const batches = {};
    const individual = [];

    subs.forEach(sub => {
      // Only group Excel-native subscriptions with batchId and _batchCount > 1
      if (subKind(sub) === 'excel' && sub.batchId && sub._batchCount > 1) {
        if (!batches[sub.batchId]) {
          batches[sub.batchId] = [];
        }
        batches[sub.batchId].push(sub);
      } else {
        individual.push(sub);
      }
    });

    return { batches, individual };
  };

  const allSubscriptions = getAllSubscriptions();
  const displayedSubscriptions = getDisplayedSubscriptions();
  const { batches, individual } = groupSubscriptionsByBatch(displayedSubscriptions);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading subscriptions...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Report Subscriptions</h2>
          <p className="text-gray-600">Manage your automated report deliveries</p>
        </div>
        <button
          onClick={onCreateNew}
          className="flex items-center px-4 py-2 bg-[var(--lodge-orange-400)] text-white rounded-lg hover:bg-[var(--lodge-orange-500)] transition-colors"
        >
          <Plus size={20} className="mr-2" />
          New Subscription
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle size={20} className="text-red-500 mr-3" />
          <span className="text-red-700">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Subscriptions List */}
      {displayedSubscriptions.length === 0 ? (
        <div className="text-center py-12">
          <Mail size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No subscriptions yet</h3>
          <p className="text-gray-600 mb-4">Create your first report subscription to get started</p>
          <button
            onClick={onCreateNew}
            className="px-4 py-2 bg-[var(--lodge-orange-400)] text-white rounded-lg hover:bg-[var(--lodge-orange-500)] transition-colors"
          >
            Create Subscription
          </button>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-auto max-h-[70vh]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Subscription', 'Timeframe', 'Attachment', 'Schedule', 'Next send', 'To', 'Status', 'Actions'].map(label => (
                    <th key={label} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Render batch groups first */}
                {Object.entries(batches).map(([batchId, batchSubs]) =>
                  renderBatchGroupRow(batchId, batchSubs)
                )}

                {/* Render individual subscriptions */}
                {individual.map(sub => renderSubscriptionRow(sub, subKind(sub), false))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* NativePages schedule editor */}
      {editingNativePages && (
        <NativePagesScheduleEditModal
          subscription={editingNativePages}
          onSaved={(updated) => {
            setSubscriptions(prev => prev.map(sub => (sub.id === updated.id ? updated : sub)));
            setEditingNativePages(null);
            toast.success('Subscription updated.', { duration: 3000 });
          }}
          onCancel={() => setEditingNativePages(null)}
        />
      )}

      {/* Test Report Date Picker Modal */}
      {testModal && createPortal(
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) setTestModal(null); }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 360, margin: '0 16px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontSize: 17, fontWeight: 600, color: '#111', marginBottom: 6 }}>Send Test Report</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>Choose the effective date for the test report data.</p>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Effective Date</label>
            <input
              type="date"
              value={testModal.date}
              onChange={(e) => setTestModal(prev => ({ ...prev, date: e.target.value }))}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, color: '#111', marginBottom: 20, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setTestModal(null)}
                style={{ flex: 1, padding: '9px 0', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', fontSize: 14, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleTestSubmit}
                disabled={!testModal.date}
                style={{ flex: 1, padding: '9px 0', border: 'none', borderRadius: 8, background: '#f97316', color: '#fff', fontSize: 14, fontWeight: 600, cursor: testModal.date ? 'pointer' : 'not-allowed', opacity: testModal.date ? 1 : 0.5 }}
              >
                Send Test
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default UnifiedSubscriptionManagement;
